import SingleTag from "@/components/Tags/tag.component";
import { ROUTE_INBOX } from "@/constants/routes.constants";
import { useUser } from "@/hooks/AppContext/user.hook";
import { sendNotificationRead } from "@/models/notifications.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import { NewTaskTag, UserNotification } from "@/types";
import {
  formatDueDate,
  genericFailureToast,
  genericSuccessToast,
  getNewTaskTagByID,
  getNumArrayFromString,
} from "@/utils/utils";
import { format, isToday, isYesterday } from "date-fns";
import { CheckCheckIcon, User } from "lucide-react";
import React, { useContext } from "react";
import { useNavigate } from "react-router";

type SingleLineNotificationProps = {
  notification: UserNotification;
  removeNotification: (notificationID: number) => void;
};

const SingleLineNotification: React.FC<SingleLineNotificationProps> = ({
  notification,
  removeNotification,
}) => {
  const { precacheTasks, userTags } = useContext(AppContext);
  const nav = useNavigate();
  const notificationTask = precacheTasks?.find(
    (task) => task.id === notification.task
  );

  const { getUserFullNameByID, getUserProfileImageByID } = useUser();

  // console.log(notification);

  async function markAsRead() {
    console.log("Mark as read", notification.id);
    const response = await sendNotificationRead({
      notificationID: notification.id,
    });
    console.log(response);

    if (response.rStatus === "error") {
      console.error(response.message);
      genericFailureToast("Failed to mark notification as read");
      return;
    }

    genericSuccessToast("Notification marked as read");
    removeNotification(notification.id);
  }

  function formatNotificationDate(date: string) {
    try {
      if (!date) {
        return "No date";
      }

      const dDate = new Date(date);
      if (isNaN(dDate.getTime())) {
        return "Invalid Date";
      }

      let dateShown;
      if (isToday(dDate)) {
        dateShown = format(dDate, "HH:mm:ss");
      } else if (isYesterday(dDate)) {
        dateShown = `Yesterday ${format(dDate, "HH:mm:ss")}`;
      } else {
        dateShown = format(dDate, " HH:mm:ss yyyy/MM/dd");
      }

      return dateShown;
    } catch (error) {
      console.error(error);
      return "Invalid Date";
    }
  }

  function getTagsFromIDString(str: string): NewTaskTag[] {
    // getNewTaskTagByID(tag.id, userTags);
    return getNumArrayFromString(str)
      .map((tagID) => getNewTaskTagByID(tagID, userTags))
      .filter((tag): tag is NewTaskTag => tag !== undefined);
  }

  return notificationTask ? (
    <div
      onClick={() => {
        nav(`${ROUTE_INBOX}/${notificationTask.id}`);
      }}
      className="w-full hover:bg-accent hover:text-accent-foreground p-3 outline outline-1 outline-accent cursor-pointer m-0"
    >
      <div className="flex justify-between content-center w-full">
        <div className="content-center flex gap-2">
          <div className="bg-neutral-300 dark:bg-neutral-900 my-1 rounded text-sm flex flex-row px-2 py-1 justify-between items-center content-center">
            <div className="flex items-center gap-2">
              {getUserProfileImageByID(notification.sender) ? (
                <img
                  className="size-4 rounded-full bg-primary shrink-0"
                  src={getUserProfileImageByID(notification.sender)}
                  alt={getUserFullNameByID(notification.sender)}
                />
              ) : (
                <User className="size-4 rounded-full bg-primary shrink-0" />
              )}

              <div className="font-bold shrink-0 w-fit truncate">
                {getUserFullNameByID(notification.sender)}
              </div>
            </div>
          </div>
          <div className="content-center">
            {formatNotificationDate(notification.notification_date)}
          </div>
        </div>

        {notification.status === "Incomplete" && (
          <Button
            variant={"outline"}
            onClick={(e) => {
              e.stopPropagation();
              markAsRead();
            }}
            className="content-center"
          >
            <CheckCheckIcon className="w-4 h-4" />
            Mark Read
          </Button>
        )}
      </div>

      <div className="font-bold text-primary truncate max-w-[200px] lg:max-w-[300px] 2xl:max-w-[600px]">
        {notificationTask.title}
      </div>
      <div>
        {/* field: "status" | "parent_id" | "title" | "description" | "assigned_to" | "collaborators" | "due_date" | "tags"; */}
        {notification.activity.map((act, index) => (
          <div
            key={index}
            className="flex flex-row gap-2 content-center items-center max-w-[300px] lg:max-w-[400px] 2xl:max-w-[800px] overflow-hidden"
          >
            {act.field === "due_date" ? (
              <div>
                Change the due date to {formatDueDate(act.value) || "No Date"}
              </div>
            ) : act.field === "status" ? (
              <div>Marked task {act.value}</div>
            ) : act.field === "assigned_to" ? (
              <div className="flex content-center items-center">
                <div className="mr-2">Assigned to</div>
                <div className="bg-neutral-300 dark:bg-neutral-900 my-1 rounded text-sm flex flex-row px-2 py-1 justify-between items-center">
                  <div className="font-bold">
                    <div className="flex items-center gap-2">
                      {getUserProfileImageByID(act.value) ? (
                        <img
                          className="size-4 rounded-full bg-primary shrink-0"
                          src={getUserProfileImageByID(act.value)}
                          alt={getUserFullNameByID(act.value)}
                        />
                      ) : (
                        <User className="size-4 rounded-full bg-primary shrink-0" />
                      )}

                      <div className="font-bold shrink-0 w-fit truncate">
                        {getUserFullNameByID(act.value)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : act.field === "collaborators" ? (
              <div className="flex flex-wrap gap-2">
                <div className="">Updated collaborators to</div>
                {getNumArrayFromString(act.value).map((collab, index) => {
                  return (
                    <div
                      key={index}
                      className="bg-neutral-300 dark:bg-neutral-900 my-1 rounded text-sm flex flex-row px-2 py-1 justify-between items-center"
                    >
                      <div className="flex items-center gap-2">
                        {getUserProfileImageByID(collab) ? (
                          <img
                            className="size-4 rounded-full bg-primary shrink-0"
                            src={getUserProfileImageByID(collab)}
                            alt={getUserFullNameByID(collab)}
                          />
                        ) : (
                          <User className="size-4 rounded-full bg-primary shrink-0" />
                        )}

                        <div className="font-bold shrink-0 w-fit truncate">
                          {getUserFullNameByID(collab)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : act.field === "tags" ? (
              <div className="flex content-center items-center gap-2 flex-wrap">
                <div>Updated Tags to</div>
                {getTagsFromIDString(act.value).map((tag) => {
                  return (
                    <SingleTag
                      key={tag.tag + tag.category_id}
                      id={tag.tag + tag.category_id}
                      name={tag.tag}
                      color={tag.color}
                      full={false}
                    />
                  );
                })}
              </div>
            ) : act.field === "description" ? (
              <div className="w-full truncate">Updated Description</div>
            ) : act.field === "parent_id" ? (
              <div>Updated parent task to {act.value}</div>
            ) : act.field === "title" ? (
              <div className="truncate">Updated title to {act.value}</div>
            ) : (
              <div>
                {act.field}: {act.value}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div>
      <div>Task {notification.task} not found</div>
    </div>
  );
};

export default SingleLineNotification;
